import { CCard, CCardBody, CCardHeader, CCol, CRow } from "@coreui/react";
import { CChartBar } from "@coreui/react-chartjs";
import React, { useEffect, useMemo, useState } from "react";
import { getCoverage, getRatio } from "src/services/StockRatio";
import ls from "local-storage";
import { CModal, CModalHeader, CModalTitle, CModalBody, CSpinner } from "@coreui/react";
import DataTable from "src/components/DataTable";
import { useNavigate } from "react-router-dom";

const StockLatest = ({ smdId: smdIdProp, provinceId: provinceIdProp, provId: provIdProp, cityId: cityIdProp }) => {
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [storesLoading, setStoresLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const navigate = useNavigate();

  // Compute effective filter values from props or local-storage
  const { effectiveSmdId, effectiveProvinceId, effectiveCityId } = useMemo(() => {
    // Prefer explicit props if provided
    const smdIdFromProps = typeof smdIdProp !== "undefined" && smdIdProp !== null ? String(smdIdProp) : undefined;
    const provinceIdFromProps = typeof (provinceIdProp ?? provIdProp) !== "undefined" && (provinceIdProp ?? provIdProp) !== null
      ? String(provinceIdProp ?? provIdProp)
      : undefined;
    const cityIdFromProps = typeof cityIdProp !== "undefined" && cityIdProp !== null ? String(cityIdProp) : undefined;

    if (smdIdFromProps || provinceIdFromProps || cityIdFromProps) {
      return {
        effectiveSmdId: smdIdFromProps ?? "0",
        effectiveProvinceId: provinceIdFromProps ?? "0",
        effectiveCityId: cityIdFromProps ?? "0",
      };
    }

    // Fallback to SalesPicker selections stored in local-storage
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");

    const computeId = (obj) => (obj?.id && obj.id !== "999" && obj.value !== "all" ? String(obj.id) : "0");

    return {
      effectiveSmdId: computeId(smd),
      effectiveProvinceId: computeId(prov),
      effectiveCityId: computeId(city),
    };
  }, [smdIdProp, provinceIdProp, provIdProp, cityIdProp]);

  useEffect(() => {
    async function fetchStock() {
      const res = await getRatio({
        smdId: effectiveSmdId,
        provinceId: effectiveProvinceId,
        cityId: effectiveCityId,
      });
      if (res.success === 1) {
        setData(res.data.map((item) => item.store_count));
      } else {
        setData([]);
      }
    }

    fetchStock();
  }, [effectiveSmdId, effectiveProvinceId, effectiveCityId]);

  const handleBarClick = async (evt, elements) => {
    if (!elements || elements.length === 0) return;
    const barIndex = elements[0].index;
    console.log(evt, elements)

    let title = "";
    if (barIndex === 0) {
      title = "Stores with Stock Ratio < 50%";
    } else if (barIndex === 1) {
      title = "Stores with Stock Ratio 50% - 75%";
    } else if (barIndex === 2) {
      title = "Stores with Stock Ratio > 75%";
    }

    setModalTitle(title);
    setModalOpen(true);
    setStoresLoading(true);

    try {
      const res = await getCoverage({
        smdId: effectiveSmdId,
        provinceId: effectiveProvinceId,
        cityId: effectiveCityId,
        index: barIndex
      });
      console.log(res)

      setStores(res.data);
    } catch (error) {
      console.error("Error fetching stores for coverage:", error);
      setStores([]);
    } finally {
      setStoresLoading(false);
    }
  };

  return (
    <>
      <CCard className="mb-4">
        <CCardHeader style={{ padding: "0.5rem 0.75rem" }}>
          <CRow className="align-items-center">
            <CCol>
              <span style={{ fontWeight: 600, fontSize: 14 }}>Latest Stock</span>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody style={{ padding: "0.5rem 0.75rem" }}>
          <CChartBar
            style={{ height: 180 }}
            data={{
              labels: ["<50%", "50%-75%", ">75%"],
              datasets: [
                {
                  label: "Stores Count",
                  backgroundColor: ["#fa5252", "#fab005", "#40c057"],
                  barThickness: 20,
                  categoryPercentage: 0.7,
                  barPercentage: 0.8,
                  data: data,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              layout: { padding: 8 },
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  intersect: false,
                },
              },
              elements: {
                bar: { borderRadius: 4 },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { display: false },
                  ticks: { display: false },
                  border: { display: false },
                },
                x: {
                  grid: { display: false },
                  ticks: { font: { size: 11 } },
                  border: { display: false },
                },
              },
              onClick: handleBarClick,
            }}
          />
        </CCardBody>
      </CCard>

      <CModal size="xl" alignment="center" scrollable visible={modalOpen} onClose={() => setModalOpen(false)}>
        <CModalHeader>
          <CModalTitle>{modalTitle}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ padding: "1rem" }}>
          {storesLoading ? (
            <div className="d-flex justify-content-center py-4">
              <CSpinner />
            </div>
          ) : (
            <DataTable
              columns={[
                { field: "store_name", headerName: "Store", flex: 1 },
              ]}
              rows={stores}
              loading={storesLoading}
              getRowId={(row) => row.store_id}
              onRowClick={(params) =>
                navigate(`/stores/details/${params.row.store_id}`)
              }
              style={{ background: "white" }}
              density="comfortable"
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooterSelectedRowCount
              pageSizeOptions={[10, 20, 50]}
              sx={{
                borderRadius: 1,
                border: "1px solid #e9ecef",
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8f9fa',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f5f5f5',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-cell': {
                  borderBottomColor: '#f1f3f5',
                },
              }}
            />
          )}
        </CModalBody>
      </CModal>
    </>
  );
};

export default StockLatest;
